import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectSchedule } from '../hooks/useProjectSchedule';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useOffices } from '../hooks/useOffices';
import { useServiceLines } from '../hooks/useServiceLines';
import { ProjectScheduleGrid } from '../components/schedule/ProjectScheduleGrid';
import { ScheduleFilters, type Density } from '../components/schedule/ScheduleFilters';
import { SavedFiltersMenu } from '../components/SavedFiltersMenu';
import { StaleScheduleNotice } from '../components/schedule/StaleScheduleNotice';
import { useScheduleFilterState } from '../hooks/useScheduleFilterState';
import { useIsMobile } from '../hooks/useIsMobile';
import { ProjectScheduleMobilePage } from './mobile/ProjectScheduleMobilePage';

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateStr(d: Date) {
  return d.toISOString().slice(0, 10);
}

function densityToVisibleDays(d: Density): number {
  return d === 'day' ? 1 : d === 'week' ? 7 : 30;
}

/**
 * Phones get a different page, not a squeezed grid — see {@link ProjectScheduleMobilePage}.
 * The branch is a whole component swap so the desktop grid's queries and measurement effects
 * never run on a phone (and vice versa); rotating a tablet past 768px switches live.
 */
export function ProjectSchedulePage() {
  return useIsMobile() ? <ProjectScheduleMobilePage /> : <ProjectScheduleDesktopPage />;
}

function ProjectScheduleDesktopPage() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();

  const [density, setDensity] = useState<Density>('week');
  const [windowStart, setWindowStart] = useState(() => new Date());
  const { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds } =
    useScheduleFilterState();
  const { data: offices } = useOffices();
  const { data: serviceLines } = useServiceLines();

  const visibleDays = densityToVisibleDays(density);
  const from = toDateStr(windowStart);
  const to = toDateStr(addDays(windowStart, visibleDays - 1));

  const query = useProjectSchedule({
    from,
    to,
    region: filters.region || undefined,
    office: filters.office || undefined,
    serviceLine: filters.serviceLine || undefined,
    personIds,
    projectIds,
  });

  const data = query.data;
  const rows = data?.rows ?? [];
  const days = data?.days ?? [];

  const regions = Array.from(
    new Set(rows.flatMap((r) => r.cells).flatMap((c) => c.items).map((i) => i.region).filter(Boolean) as string[]),
  );

  function handleToday() {
    setWindowStart(new Date());
  }
  function handlePrev() {
    setWindowStart((prev) => addDays(prev, -densityToVisibleDays(density)));
  }
  function handleNext() {
    setWindowStart((prev) => addDays(prev, densityToVisibleDays(density)));
  }
  function handleWindowStartChange(d: string) {
    setWindowStart(new Date(d + 'T00:00:00'));
  }

  // flex-1, not h-full — see SchedulePage: h-full collapses under the flex <main>.
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <ScheduleFilters
        regions={regions}
        offices={(offices ?? []).map((o) => o.name)}
        serviceLines={(serviceLines ?? []).map((s) => s.name)}
        value={filters}
        onChange={changeFilters}
        density={density}
        onDensityChange={setDensity}
        onTodayClick={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        windowStart={from}
        onWindowStartChange={handleWindowStartChange}
        filterMenu={
          <SavedFiltersMenu
            applied={appliedFilter}
            onApply={applySavedFilter}
            currentPeople={filters.people}
            currentProjects={filters.projects}
          />
        }
      />

      <StaleScheduleNotice />

      {query.isLoading && (
        <div className="flex-1 p-4">
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-1 animate-pulse">
                <div className="w-60 h-9 bg-slate-100 rounded" />
                {Array.from({ length: 20 }).map((__, j) => (
                  <div key={j} className="w-[52px] h-9 bg-slate-100 rounded" />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {query.error && (
        <div role="alert" className="m-4 bg-red-50 border border-red-200 rounded p-4 text-sm text-red-900">
          Could not load the project schedule. Try refreshing the page.
          <button
            type="button"
            onClick={() => query.refetch()}
            className="ml-3 text-brand-blue hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!query.isLoading && !query.error && rows.length === 0 && (
        <div className="m-4 text-sm text-slate-500">
          {currentUser?.isAuthenticated
            ? 'No active projects found for the selected filters.'
            : 'Your account is not linked to a crew member profile. Ask your admin to verify your email matches your name in the schedule source.'}
        </div>
      )}

      {!query.isLoading && !query.error && rows.length > 0 && (
        <ProjectScheduleGrid
          rows={rows}
          days={days}
          density={density}
          onProjectClick={(jobNumber) => navigate(`/projects/${encodeURIComponent(jobNumber)}`)}
        />
      )}
    </div>
  );
}
